import useDocumentTitle from "@/hooks/useDocumentTitle";
import { useState } from 'react';
import { SegmentationPhase } from '@segmentation/store/segmentationStore.ts';
import { IconButton } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import useSegmentationStore from '@segmentation/hooks/useSegmentationStore.ts';

export const ControlsUI = () => {
  useDocumentTitle(`Braces Designer - Teeth Segmentation Control Panel | fullName`);
  const { setSegmentationPhase, jaws } = useSegmentationStore();
  const [isUpperVisible, setIsUpperVisible] = useState(true);
  const [isLowerVisible, setIsLowerVisible] = useState(true);
  const [moveApartDistance, setMoveApartDistance] = useState(0);

  if (!jaws) {
    return null;
  }

  const handleToggleUpper = () => {
    setIsUpperVisible((previous) => {
      const next = !previous;
      jaws.upperJaw.visible = next;
      return next;
    });
  };

  const handleToggleLower = () => {
    setIsLowerVisible((previous) => {
      const next = !previous;
      jaws.lowerJaw.visible = next;
      return next;
    });
  };

  const handleMoveApart = (value: number) => {
    setMoveApartDistance(value);
    jaws.moveApart(value);
  };

  const handleSegmentTeeth = () => {
    setSegmentationPhase(SegmentationPhase.GUM_EDIT_LOWER);
  };

  return (
    <section className="flex w-full flex-col gap-5 rounded-3xl border border-primary/40 bg-content/80 p-6 text-white shadow-xl shadow-primary/20 backdrop-blur">
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Segmentation</p>
        <h2 className="text-2xl font-semibold tracking-tight">Control Panel</h2>
        <p className="text-sm text-white/70">Toggle jaw visibility and set the spacing before starting the tooth selection.</p>
      </header>

      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-2xl border border-content-highlight/50 bg-content-dark/60 px-4 py-3">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Upper jaw</span>
            <span className="text-sm text-white/90">Currently {isUpperVisible ? 'visible' : 'hidden'}</span>
          </div>
          <IconButton
            onClick={handleToggleUpper}
            variant={isUpperVisible ? 'ghost-light' : 'ghost-primary'}
            size="sm"
            className="rounded-2xl px-4 text-xs font-semibold uppercase tracking-[0.3em]"
          >
            {isUpperVisible ? 'Hide' : 'Show'}
          </IconButton>
        </div>

        <div className="flex items-center justify-between rounded-2xl border border-content-highlight/50 bg-content-dark/60 px-4 py-3">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Lower jaw</span>
            <span className="text-sm text-white/90">Currently {isLowerVisible ? 'visible' : 'hidden'}</span>
          </div>
          <IconButton
            onClick={handleToggleLower}
            variant={isLowerVisible ? 'ghost-light' : 'ghost-primary'}
            size="sm"
            className="rounded-2xl px-4 text-xs font-semibold uppercase tracking-[0.3em]"
          >
            {isLowerVisible ? 'Hide' : 'Show'}
          </IconButton>
        </div>

        <div className="rounded-2xl border border-content-highlight/40 bg-content-dark/60 px-4 py-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.3em] text-white/60">Move apart</span>
            <span className="text-sm font-semibold text-white">{moveApartDistance.toFixed(1)} mm</span>
          </div>
          <Slider
            value={[moveApartDistance]}
            min={0}
            max={10}
            step={0.5}
            onValueChange={(value) => handleMoveApart(value[0] ?? 0)}
            className="w-full"
          />
        </div>
      </div>

      <IconButton
        onClick={handleSegmentTeeth}
        variant="ghost-primary"
        size="lg"
        className="w-full rounded-2xl border border-primary/50 bg-primary/80 text-sm font-semibold uppercase tracking-[0.3em] hover:bg-primary"
      >
        Continue to gum editing
      </IconButton>
    </section>
  );
};
